/** One feed's progress through its own newest-first pages. */
export interface FeedProgress<K extends string> {
  key: K;
  /** `createdAt` (ms) of the oldest item loaded so far; null when nothing is loaded. */
  oldestAt: number | null;
  hasMore: boolean;
}

/**
 * How far down a merged, newest-first list is trustworthy when its feeds page
 * independently.
 *
 * A feed with more to load may still hold items newer than the oldest item
 * another feed has loaded, so anything older than the *newest* of those
 * "oldest loaded" times could be missing neighbours from the other feed and
 * must stay hidden until it has caught up. `cutoff` is that time (null: every
 * feed is exhausted, show everything). `next` is the feed to load from to move
 * the cutoff down, which is the one that has gone back the least.
 */
export function pagingWindow<K extends string>(
  feeds: FeedProgress<K>[],
): { cutoff: number | null; next: K | null } {
  let cutoff: number | null = null;
  let next: K | null = null;
  for (const feed of feeds) {
    if (!feed.hasMore || feed.oldestAt === null) continue;
    if (cutoff === null || feed.oldestAt > cutoff) {
      cutoff = feed.oldestAt;
      next = feed.key;
    }
  }
  return { cutoff, next };
}
